import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { Letterhead } from './Letterhead';
import type { PrintMeta } from './types';

interface DocFrameProps {
  title: string;
  meta: PrintMeta;
  /** The document has a barcode and will signal ready itself, after the bars are drawn. */
  hasBarcode: boolean;
  onReady?: () => void;
  children: ReactNode;
}

/** Letterhead, title, body, footer: the parts every document shares. */
export function DocFrame({ title, meta, hasBarcode, onReady, children }: DocFrameProps): ReactNode {
  const { t } = useTranslation();
  const readyRef = useRef(onReady);
  readyRef.current = onReady;
  useEffect(() => {
    if (!hasBarcode) readyRef.current?.();
  }, [hasBarcode]);
  return (
    <article className="print-doc">
      <Letterhead data={meta.letterhead} />
      <h1 className="print-title">{title}</h1>
      {children}
      <footer className="print-footer">
        {meta.letterhead.footer === null ? null : <div>{meta.letterhead.footer}</div>}
        <div className="print-small">
          {t('print.printed', { at: formatDateTime(meta.printedAt), user: meta.printedBy })}
        </div>
      </footer>
    </article>
  );
}

export function Row({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="print-row">
      <span>{label}</span>
      <span className="print-amount">{value}</span>
    </div>
  );
}
